import { Box } from "@mui/material";
import { type SessionUser, type Tenant, getMyAssignments } from "../../data/api";
import { useLoad } from "../../data/data";
import { Mono, PageHeader, Section, Tag, Tags } from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import { ChangePassword } from "./ChangePassword";

// ---
// The signed-in account: who the session is, where it holds roles, and its password.
// ---

export function Account({
  user,
  tenants,
  scopes,
}: {
  user: SessionUser;
  tenants: Tenant[];
  scopes: string[];
}): React.JSX.Element {
  const tokens = useTokens();
  // Asked of the server, not read off a token: a token carries the permissions a role granted,
  // for one audience, and never the role's name. Only the assignment rows know what is held.
  const held = useLoad(getMyAssignments, []);
  const rows: [string, React.ReactNode][] = [
    ["Signed in as", user.username ?? user.email],
    ["Email", user.email],
    ["Subject", <Mono key="subject">{user.id}</Mono>],
    [
      "Tenants",
      tenants.length === 0 ? (
        "None yet"
      ) : (
        <Tags key="tenants">
          {tenants.map((tenant) => (
            <Tag key={tenant.id} label={tenant.id} />
          ))}
        </Tags>
      ),
    ],
    [
      "Roles",
      held.pending ? (
        "Loading…"
      ) : held.error !== null ? (
        held.error
      ) : (held.data ?? []).length === 0 ? (
        "None"
      ) : (
        <Tags key="roles">
          {(held.data ?? []).map((role) => {
            const where = Object.entries(role.dimensionConstraints)
              .map(([dim, value]) => `${dim}=${value}`)
              .join(" ");
            return (
              <Tag
                key={`${role.tenantId}:${role.roleKey}:${where}`}
                label={`${role.tenantId} · ${role.roleKey}${where === "" ? "" : ` · ${where}`}`}
              />
            );
          })}
        </Tags>
      ),
    ],
    [
      "Control-plane scopes",
      scopes.length === 0 ? (
        "None in this tenant"
      ) : (
        <Tags key="scopes">
          {scopes.map((scope) => (
            <Tag key={scope} label={scope} />
          ))}
        </Tags>
      ),
    ],
  ];

  return (
    <>
      <PageHeader title="Account" />
      <Section title="This account">
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: "180px 1fr",
            rowGap: 3,
            columnGap: 4,
            m: 0,
            fontSize: 13.5,
            "& dt": { color: tokens.ink.muted },
            "& dd": { m: 0, color: tokens.ink.primary, minWidth: 0, wordBreak: "break-all" },
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: "contents" }}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </Box>
          ))}
        </Box>
      </Section>
      <Section title="Change password">
        <ChangePassword />
      </Section>
    </>
  );
}
